import { useMemo } from 'react'
import { Box, Button } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { GridCellParams } from '@mui/x-data-grid-premium'
import { BaseTable } from 'Features/Table/BaseTable.view'
import { ExtendedGridColDef } from 'Features/Table/BulkUpdateModal'
import {
  SheduleTableProps,
  SchedulerFormatChangeoverRowType,
} from './Scheduler.types'
import { FONT_SANS, TOKENS } from './SchedulerDesign'

/**
 * Bottle-format changeovers — the time, cost and resource draw of moving a
 * line from one bottle format to another.
 *
 * Formats are **declared on the products**: the from/to columns offer exactly
 * the distinct values found in the product master-data `Format` column, which
 * is the only place a format is defined. A format is therefore never free text
 * here — if a format is missing from the list, the fix is to set it on a
 * product, not to type it in.
 *
 * A row may also name a **Resource Constraint** the reconfiguration consumes
 * while it runs, with an amount. That is what makes a format change cost more
 * than the line it happens on: a capacity-1 resource shared by two lines
 * (BTL01_BTL02) held for the duration of the change stops the sibling line
 * producing.
 *
 * Entries are directed: the reverse change is its own row and may differ. A
 * change within one format is not a changeover, so it gets no row — which is
 * what makes "same format incurs nothing" true by absence rather than by a
 * special case.
 */

const dataConfig = {
  required: ['format_from', 'format_to', 'changeover_time'],
  optional: ['changeover_cost', 'resource_constraint', 'resource_amount'],
}

type SchedulerFormatChangeoverTableProps =
  SheduleTableProps<SchedulerFormatChangeoverRowType> & {
    /** Distinct formats declared on products — the format vocabulary. */
    formatOptions: string[]
    /** Resource-constraint names a changeover can consume. */
    resourceOptions: string[]
    /** Append a blank, hand-editable row. */
    onAddRow: () => void
    /** Ids of hand-added, not-yet-saved rows — the only rows whose from/to
     *  may be edited, since those two columns are the merge key. */
    manualIds: Set<number>
  }

/** Non-negative number, or blank. Shared by both numeric columns. */
const nonNegative = (val: unknown): boolean => {
  if (val === null || val === undefined || val === '') return true
  const n = Number(val)
  return Number.isFinite(n) && n >= 0
}

export const SchedulerFormatChangeoverTable = ({
  data,
  isLoading,
  updateTableData,
  uploadScheduleData,
  isUploading,
  isSaving,
  updateRowsStatus,
  predicate,
  deleteTableData,
  isDeleting,
  formatOptions,
  resourceOptions,
  onAddRow,
  manualIds,
}: SchedulerFormatChangeoverTableProps) => {
  const handleDataUpdate = (
    changedRows: Record<number, SchedulerFormatChangeoverRowType>
  ) => {
    updateTableData(changedRows)
  }

  const columns: ExtendedGridColDef[] = useMemo(
    () => [
      {
        headerName: 'Format From',
        field: 'format_from',
        // Free text rather than a select, because the vocabulary can be empty:
        // on a schedule where the product Format column has not been filled in
        // there is nothing to pick from, and that is exactly when a planner
        // needs to be able to type a pair in. `isCellEditable` below then
        // restricts editing to hand-added rows — a scaffolded or saved row's
        // from/to is the merge key and moving it would duplicate the row
        // rather than rename it.
        type: 'string',
        flex: 1,
        editable: true,
        description:
          'The bottle format the line is changing FROM, as declared on the products. Only editable on a row you added by hand; the pre-listed rows are the pairs your products already declare. Entries are directed: the reverse change is its own row and can carry a different time, cost and resource.',
        preProcessEditCellProps: (params) => {
          const value = String(params.props.value ?? '').trim()
          const to = String(
            (params.row as SchedulerFormatChangeoverRowType)?.format_to ?? ''
          ).trim()
          // Two ways a from/to is unusable, both flagged at entry:
          // blank (it is half the merge key, so a blank one saves a row the
          // engine can only reject), and equal to the other side (a change
          // within one format is not a changeover).
          return {
            ...params.props,
            error: !value || value === to,
          }
        },
      },
      {
        headerName: 'Format To',
        field: 'format_to',
        type: 'string',
        flex: 1,
        editable: true,
        description:
          'The bottle format the line is changing TO, as declared on the products. Only editable on a row you added by hand. Spelling must match the product Format column exactly — a mismatch is not an error, it is a changeover that never fires (the run log reports how many pairs name a format no product declares). A change within the same format is not a changeover and needs no row.',
        preProcessEditCellProps: (params) => {
          const value = String(params.props.value ?? '').trim()
          const from = String(
            (params.row as SchedulerFormatChangeoverRowType)?.format_from ?? ''
          ).trim()
          return {
            ...params.props,
            error: !value || value === from,
          }
        },
      },
      {
        headerName: 'Changeover Time (hrs)',
        field: 'changeover_time',
        type: 'number',
        flex: 1,
        editable: true,
        cellClassName: 'sch-num',
        description:
          'Hours the line is unavailable while the format change is made. Added to the schedule before the first run of the new format. A row left at 0 time AND 0 cost is inert — it is skipped entirely, which is what makes the blank rows of this grid free.',
        preProcessEditCellProps: (params) => ({
          ...params.props,
          error: !nonNegative(params.props.value),
        }),
      },
      {
        headerName: 'Changeover Cost',
        field: 'changeover_cost',
        type: 'number',
        flex: 1,
        editable: true,
        cellClassName: 'sch-num',
        description:
          'Cost incurred by the format change. Reported against the schedule alongside the other changeover costs.',
        preProcessEditCellProps: (params) => ({
          ...params.props,
          error: !nonNegative(params.props.value),
        }),
      },
      {
        // The reason this column exists: a format change is not free to the
        // rest of the plant. Naming a resource here books it for the whole
        // reconfiguration, so anything else needing it waits — which for a
        // capacity-1 resource shared by two lines means the sibling line
        // stops. Blank is the common case and consumes nothing.
        headerName: 'Resource Constraint',
        field: 'resource_constraint',
        type: 'singleSelect',
        valueOptions: resourceOptions,
        flex: 1,
        editable: true,
        description:
          'Resource the format change consumes while it runs, from Resource & Material Constraints. Held for the whole changeover, so anything else needing it must wait — a capacity-1 resource shared by two lines (e.g. BTL01_BTL02) means changing one line stops the other. Leave blank if the change consumes nothing.',
      },
      {
        headerName: 'Resource Amount',
        field: 'resource_amount',
        type: 'number',
        flex: 1,
        editable: true,
        cellClassName: 'sch-num',
        description:
          'Units of the resource consumed per hour, checked against its capacity exactly as a production run is. Blank means 1 — a single unit, which fully occupies a capacity-1 resource. An amount above the resource capacity is rejected by the engine, since such a changeover could never be scheduled.',
        preProcessEditCellProps: (params) => ({
          ...params.props,
          error: !nonNegative(params.props.value),
        }),
      },
    ],
    [formatOptions, resourceOptions]
  )

  // From/to are the row's merge key: changing them on a saved or scaffolded
  // row would insert a second row rather than move the existing one, so they
  // are editable only while the row is one the planner just added. Every other
  // column is editable everywhere.
  const isCellEditable = (params: GridCellParams) => {
    if (params.field === 'format_from' || params.field === 'format_to') {
      return manualIds.has(Number(params.id))
    }
    return true
  }

  return (
    <Box>
      <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={onAddRow}
          sx={{ textTransform: 'none' }}
        >
          Add changeover
        </Button>
        {formatOptions.length === 0 && (
          // The dead-end case: no product declares a Format, so there are no
          // pairs to scaffold and the grid is empty. Say why, and where to fix
          // it — an empty table with no explanation reads as broken.
          <Box
            sx={{
              fontSize: 11.5,
              fontFamily: FONT_SANS,
              color: TOKENS.ink500,
            }}
          >
            No product declares a Format yet, so there are no pairs to list.
            Set the <strong>Format</strong> column on the Production Master Data
            tab and the transitions between those values appear here — or add a
            row by hand.
          </Box>
        )}
      </Box>
      <BaseTable
        data={data ?? []}
        columns={columns}
        isLoading={isLoading}
        updateTableData={handleDataUpdate}
        uploadData={uploadScheduleData}
        isUploading={isUploading}
        isSaving={isSaving}
        isCellEditable={isCellEditable}
        checkBoxSelection={true}
        showTableToolBar={true}
        downloadFileName="SchedulerFormatChangeover"
        onDeleteRows={deleteTableData}
        isDeleting={isDeleting}
        multiStageUpload={{
          uploadFile: uploadScheduleData,
          isUploading: isUploading,
          columns: columns,
          updateRowsStatus: updateRowsStatus,
          originalData: data,
          predicate: predicate,
          dataConfig: dataConfig,
        }}
        initialState={{
          pagination: { paginationModel: { pageSize: 100 } },
        }}
        sx={{
          maxWidth: 'calc(100vw - 70px)',
          '& .MuiDataGrid-cell--editable': {
            backgroundColor: '#CBD5E1',
          },
          '& .MuiBox-root': {
            padding: '0px !important',
          },
        }}
      />
    </Box>
  )
}
